// MappletSidebar.js
import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback
} from "react";
import { DomEvent } from "leaflet";
import { useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faHome,
  faSearch,
  faCog,
  faLocationArrow,
  faFilter,
  faMap
} from "@fortawesome/free-solid-svg-icons";
import parse from "html-react-parser";

import { Sidebar, Tab } from "../../utils/Sidetabs";
import { MapContext } from "../MapContext";
import AutoCompleteText from "../AutocompleteText";
import MapFilter from "../MapFilter";
import MapLayers from "../MapLayers";

const refContainer = (element) => {
  if (element) {
    DomEvent.disableClickPropagation(element).disableScrollPropagation(element);
  }
};

const MapSidebar = forwardRef(({ updateFeatureEvents, setJsonFilter, initSidebar, handleLayers }, ref) => {
  const { t } = useTranslation();
  const map = useMap();

  const [state, setState] = useState({
    collapsed: true,
    selected: "home",
  });

  const [filterValuesChanged, setFilterValuesChanged] = useState(false);
  const [POI, setPOI] = useState("<span>Places</span>");

  // Uso di useRef per mantenere lo stato di "primo accesso" tra i re-render
  const isFirstTime = useRef(true);

  const {
    searchFeatures,
    initFile,
    mapFilterRef,
  } = useContext(MapContext);

  useEffect(() => {
    if (state.selected === "layers") {
      setFilterValuesChanged(true);
    }
  }, [state.selected]);

  useImperativeHandle(ref, () => ({
    updatePlaces(places) {
      setPOI(places);
    },
  }));

  useEffect(() => {
    updateFeatureEvents();
  }, [POI, updateFeatureEvents]);

  const onSidebarClose = useCallback(() => {
    setState((prevState) => ({ ...prevState, collapsed: true }));
  }, []);

  const onSidebarOpen = useCallback((id) => {
    setState({
      collapsed: false,
      selected: id,
    });

    if (isFirstTime.current && id === "places") {
      initSidebar();
      isFirstTime.current = false;
    }
  }, [initSidebar]);

  return (
    <div ref={refContainer}>
      <Sidebar
        id="sidebar"
        position="left"
        collapsed={state.collapsed}
        closeIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        selected={state.selected}
        onOpen={onSidebarOpen}
        onClose={onSidebarClose}
      >
        <Tab
          id="home"
          key="home"
          header="Home"
          icon={<FontAwesomeIcon icon={faHome} title={t("Home")} />}
        >
          <p>mappletFilename</p>
        </Tab>

        <Tab
          id="layers"
          key="layers"
          header={t("Layers")}
          icon={<FontAwesomeIcon icon={faMap} title={t("Layers")} />}
        >
          <MapLayers
            setJsonFilter={setJsonFilter}
            handleLayers={handleLayers}
            filterValuesChanged={filterValuesChanged}
            setFilterValuesChanged={setFilterValuesChanged}
          />
        </Tab>

        <Tab
          id="search"
          key="search"
          header={t("Search")}
          icon={<FontAwesomeIcon icon={faSearch} title={t("Search")} />}
        >
          <div className="app-component">
            <AutoCompleteText
              initFile={initFile}
              searchFeatures={searchFeatures}
              layers={initFile?.jsonLayers}
              map={map}
              t={t}
              selected={state.selected}
            />
          </div>
        </Tab>

        <Tab
          id="places"
          key="places"
          header={t("Places")}
          icon={<FontAwesomeIcon icon={faLocationArrow} title={t("Places")} />}
        >
          <span>{parse(POI)}</span>
        </Tab>

        <Tab
          id="filter"
          key="filter"
          header={t("Filter")}
          icon={<FontAwesomeIcon icon={faFilter} title={t("Filter")} />}
        >
          <div className="app-component">
            <MapFilter ref={mapFilterRef} setJsonFilter={setJsonFilter} />
          </div>
        </Tab>

        <Tab
          id="settings"
          key="settings"
          header={t("Settings")}
          anchor="bottom"
          icon={<FontAwesomeIcon icon={faCog} title={t("Settings")} />}
        >
          <p>{t("Settings content")}</p>
        </Tab>
      </Sidebar>
    </div>
  );
});

MapSidebar.displayName = "MapSidebar";

export default MapSidebar;
